import { z } from "zod";
import { createCodec } from "../codec";

const DynamicFieldType = z.enum([
  "number",
  "string",
  "email",
  "rfc",
  "curp",
  "select",
]);

const DynamicFieldApiSchema = z.object({
  id: z.number(),
  name: z.string(),
  type_field: DynamicFieldType,
  length_field: z.number().int().positive(),
  required_field: z.union([z.literal(0), z.literal(1)]),
  value_field: z.string().optional().nullable(),
  options_select: z.array(z.string()).optional().nullable(),
});

const DynamicFieldCodec = createCodec({
  apiSchema: DynamicFieldApiSchema,
  deserialize: (api) => ({
    id: api.id,
    name: api.name,
    type: api.type_field,
    maxLength: api.length_field,
    required: api.required_field === 1,
    value: api.value_field,
    options: api.options_select,
  }),
  serialize: (app) => ({
    id: app.id,
    name: app.name,
    type_field: app.type,
    length_field: app.maxLength,
    required_field: (app.required ? 1 : 0) as 0 | 1,
    value_field: app.value,
    options_select: app.options,
  }),
});

type DynamicField = z.output<typeof DynamicFieldCodec.schema>;
type DynamicFieldType = z.infer<typeof DynamicFieldType>;
type DynamicFieldApi = z.input<typeof DynamicFieldCodec.schema>;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RFC_REGEX = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/i;
const CURP_REGEX = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z\d]\d$/i;

const dynamicFieldName = (id: number) => `dynamic_${id}`;

// Translator for the customer namespace's `dynamicErrors.*` keys, passed in
// from the form component (schemas are module-scope and can't use hooks).
type DynamicErrorTranslator = (
  key: string,
  values?: Record<string, string | number>,
) => string;

const buildDynamicFieldSchema = (
  field: DynamicField,
  t: DynamicErrorTranslator,
) =>
  z.string().superRefine((raw, ctx) => {
    const value = raw.trim();

    if (!value) {
      if (field.required) {
        ctx.addIssue({
          code: "custom",
          message: t("dynamicErrors.required", { name: field.name }),
        });
      }
      return;
    }

    if (value.length > field.maxLength) {
      ctx.addIssue({
        code: "custom",
        message: t("dynamicErrors.maxLength", { max: field.maxLength }),
      });
    }

    switch (field.type) {
      case "number":
        if (!/^\d+$/.test(value)) {
          ctx.addIssue({
            code: "custom",
            message: t("dynamicErrors.invalid", { name: field.name }),
          });
        }
        break;
      case "email":
        if (!EMAIL_REGEX.test(value)) {
          ctx.addIssue({
            code: "custom",
            message: t("dynamicErrors.emailInvalid"),
          });
        }
        break;
      case "rfc":
        if (!RFC_REGEX.test(value)) {
          ctx.addIssue({
            code: "custom",
            message: t("dynamicErrors.rfcInvalid"),
          });
        }
        break;
      case "curp":
        if (!CURP_REGEX.test(value)) {
          ctx.addIssue({
            code: "custom",
            message: t("dynamicErrors.curpInvalid"),
          });
        }
        break;
      case "select":
        if (field.options && !field.options.includes(value)) {
          ctx.addIssue({
            code: "custom",
            message: t("dynamicErrors.invalid", { name: field.name }),
          });
        }
        break;
    }
  });

const buildDynamicFieldsShape = (
  fields: DynamicField[],
  t: DynamicErrorTranslator,
) =>
  Object.fromEntries(
    fields.map((field) => [
      dynamicFieldName(field.id),
      buildDynamicFieldSchema(field, t),
    ]),
  );

const dynamicFieldDefaults = (fields: DynamicField[]): Record<string, string> =>
  Object.fromEntries(
    fields.map((field) => [dynamicFieldName(field.id), field.value ?? ""]),
  );

const collectDynamicFieldValues = (
  fields: DynamicField[],
  values: Record<string, string>,
): DynamicField[] =>
  fields.map((field) => ({
    ...field,
    value: values[dynamicFieldName(field.id)]?.trim() ?? "",
  }));

export {
  DynamicFieldCodec,
  DynamicFieldType,
  dynamicFieldName,
  buildDynamicFieldsShape,
  dynamicFieldDefaults,
  collectDynamicFieldValues,
  type DynamicField,
  type DynamicFieldApi,
};
